import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import type { FunnelCohort, FunnelCohortCondition, UserTraitValueOption } from '../types';
import { useCohorts } from '../hooks/useCohorts';
import { COHORT_BUILDER_TEST_IDS } from '../testIds';

interface CohortBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Trait key → labeled value options (I-B1: labels display-only, raw values stored). */
  userTraitOptions?: Record<string, UserTraitValueOption[]> | null;
  /** Extra trait keys (e.g. from filter options) merged into the key picker. */
  availableTraitKeys?: string[];
  /** Applies the cohort's conditions as the active trait filter. */
  onApplyCohort?: (cohort: FunnelCohort) => void;
}

interface DraftCondition {
  traitKey: string;
  values: string[];
}

const EMPTY_DRAFT: { name: string; conditions: DraftCondition[] } = {
  name: '',
  conditions: [{ traitKey: '', values: [] }],
};

function draftIsValid(name: string, conditions: DraftCondition[]): boolean {
  const complete = conditions.filter(condition => condition.traitKey && condition.values.length);
  return Boolean(name.trim()) && complete.length > 0 && complete.length === conditions.length;
}

export const CohortBuilderDialog: React.FC<CohortBuilderDialogProps> = ({
  open,
  onClose,
  projectId,
  userTraitOptions,
  availableTraitKeys,
  onApplyCohort,
}) => {
  const { cohorts, loading, error, createCohort, updateCohort, deleteCohort, estimateConditions } = useCohorts({
    projectId,
    enabled: open,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState(EMPTY_DRAFT.name);
  const [conditions, setConditions] = useState<DraftCondition[]>(EMPTY_DRAFT.conditions);
  const [estimate, setEstimate] = useState<number | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const estimateAbort = useRef<AbortController | null>(null);

  const traitKeys = useMemo(
    () => Array.from(new Set([
      ...Object.keys(userTraitOptions ?? {}),
      ...(availableTraitKeys ?? []),
    ])).sort((a, b) => a.localeCompare(b)),
    [userTraitOptions, availableTraitKeys],
  );

  const completeConditions = useMemo(
    () => conditions.filter(condition => condition.traitKey && condition.values.length),
    [conditions],
  );

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setName(EMPTY_DRAFT.name);
      setConditions(EMPTY_DRAFT.conditions);
      setEstimate(null);
      setSaveError(null);
    }
  }, [open]);

  // Debounced live size estimate for the currently complete conditions.
  useEffect(() => {
    estimateAbort.current?.abort();
    if (!open || !completeConditions.length) {
      setEstimate(null);
      return;
    }

    const controller = new AbortController();
    estimateAbort.current = controller;
    const timer = setTimeout(() => {
      setEstimating(true);
      estimateConditions(completeConditions as FunnelCohortCondition[], controller.signal)
        .then(result => setEstimate(result.users))
        .catch(() => {
          if (!controller.signal.aborted) setEstimate(null);
        })
        .finally(() => {
          if (!controller.signal.aborted) setEstimating(false);
        });
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, completeConditions, estimateConditions]);

  const startEdit = (cohort: FunnelCohort) => {
    setEditingId(cohort.id);
    setName(cohort.name);
    setConditions(cohort.conditions.map(condition => ({ ...condition })));
    setSaveError(null);
  };

  const resetDraft = () => {
    setEditingId(null);
    setName(EMPTY_DRAFT.name);
    setConditions([{ traitKey: '', values: [] }]);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        name: name.trim(),
        conditions: completeConditions as FunnelCohortCondition[],
      };
      if (editingId) {
        await updateCohort(editingId, payload);
      } else {
        await createCohort(payload);
      }
      resetDraft();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save cohort');
    } finally {
      setSaving(false);
    }
  };

  const updateCondition = (index: number, patch: Partial<DraftCondition>) => {
    setConditions(current => current.map((condition, i) => (i === index ? { ...condition, ...patch } : condition)));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid={COHORT_BUILDER_TEST_IDS.dialog}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Cohorts by user traits
        <IconButton size="small" onClick={onClose} aria-label="close cohort builder">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Saved cohorts
        </Typography>
        <List dense data-testid={COHORT_BUILDER_TEST_IDS.list} sx={{ mb: 1.5, maxHeight: 180, overflow: 'auto' }}>
          {!loading && cohorts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              No saved cohorts yet. Build one below.
            </Typography>
          )}
          {cohorts.map(cohort => (
            <ListItem
              key={cohort.id}
              data-testid={COHORT_BUILDER_TEST_IDS.listItem(cohort.id)}
              secondaryAction={(
                <Stack direction="row" spacing={0.5}>
                  {onApplyCohort && (
                    <Button
                      size="small"
                      data-testid={COHORT_BUILDER_TEST_IDS.applyButton(cohort.id)}
                      onClick={() => onApplyCohort(cohort)}
                    >
                      Apply
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    aria-label="edit cohort"
                    data-testid={COHORT_BUILDER_TEST_IDS.editButton(cohort.id)}
                    onClick={() => startEdit(cohort)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="delete cohort"
                    data-testid={COHORT_BUILDER_TEST_IDS.deleteButton(cohort.id)}
                    onClick={() => deleteCohort(cohort.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            >
              <ListItemText
                primary={cohort.name}
                secondary={[
                  cohort.conditions.map(condition => `${condition.traitKey} ∈ [${condition.values.join(', ')}]`).join(' AND '),
                  cohort.lastSizeEstimate ? `~${cohort.lastSizeEstimate.users} users` : null,
                ].filter(Boolean).join(' · ')}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mb: 1.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {editingId ? 'Edit cohort' : 'New cohort'}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Cohort name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          inputProps={{ 'data-testid': COHORT_BUILDER_TEST_IDS.nameInput }}
          sx={{ mb: 1.5 }}
        />

        <Stack spacing={1}>
          {conditions.map((condition, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid={COHORT_BUILDER_TEST_IDS.conditionRow(index)}
            >
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Trait</InputLabel>
                <Select
                  label="Trait"
                  value={condition.traitKey}
                  data-testid={COHORT_BUILDER_TEST_IDS.conditionKey(index)}
                  onChange={(event) => updateCondition(index, { traitKey: event.target.value, values: [] })}
                >
                  {traitKeys.map(key => (
                    <MenuItem key={key} value={key}>{key}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Values (any of)</InputLabel>
                <Select
                  multiple
                  label="Values (any of)"
                  disabled={!condition.traitKey}
                  value={condition.values}
                  data-testid={COHORT_BUILDER_TEST_IDS.conditionValues(index)}
                  onChange={(event) => {
                    const next = event.target.value;
                    updateCondition(index, { values: typeof next === 'string' ? next.split(',') : next });
                  }}
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={0.25} flexWrap="wrap" useFlexGap>
                      {selected.map(item => (
                        <Chip
                          key={item}
                          label={(userTraitOptions?.[condition.traitKey] ?? []).find(option => option.value === item)?.label ?? item}
                          size="small"
                        />
                      ))}
                    </Stack>
                  )}
                >
                  {(userTraitOptions?.[condition.traitKey] ?? []).map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                aria-label="remove condition"
                disabled={conditions.length === 1}
                data-testid={COHORT_BUILDER_TEST_IDS.conditionRemove(index)}
                onClick={() => setConditions(current => current.filter((_, i) => i !== index))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
          <Tooltip title="Conditions are combined with AND; values within a condition match with OR">
            <Button
              size="small"
              startIcon={<AddIcon />}
              data-testid={COHORT_BUILDER_TEST_IDS.conditionAdd}
              disabled={conditions.length >= 8}
              onClick={() => setConditions(current => [...current, { traitKey: '', values: [] }])}
            >
              Add condition
            </Button>
          </Tooltip>
          <Box data-testid={COHORT_BUILDER_TEST_IDS.estimate}>
            <Typography variant="body2" color="text.secondary">
              {completeConditions.length === 0
                ? 'Pick a trait and values to see cohort size'
                : estimating
                  ? 'Estimating…'
                  : estimate === null
                    ? 'Size estimate unavailable'
                    : `~${estimate} matching users`}
            </Typography>
          </Box>
        </Stack>

        {saveError && <Alert severity="error" sx={{ mt: 1.5 }} data-testid={COHORT_BUILDER_TEST_IDS.saveError}>{saveError}</Alert>}
      </DialogContent>
      <DialogActions>
        {editingId && (
          <Button onClick={resetDraft} data-testid={COHORT_BUILDER_TEST_IDS.cancelEdit}>
            Cancel edit
          </Button>
        )}
        <Button
          variant="contained"
          disabled={!draftIsValid(name, conditions) || saving}
          data-testid={COHORT_BUILDER_TEST_IDS.saveButton}
          onClick={handleSave}
        >
          {editingId ? 'Save changes' : 'Save cohort'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
